import './App.scss';

import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AuthenticationState, EvaLayout, EvaLayoutType, useAuthenticationStore } from '@engelvoelkersamericas/common-ui';

import { useEvaPostHog, useCurrencyInitialization } from '@hooks';
import { useReferenceDataStore, useTeamProfileStore } from '@stores';
import { setupDevAuthCookies } from '@utils';
import { PREFIX_OTHER_USER_URL, PREFIX_OTHER_TEAM_URL, SCREEN_BREAKPOINTS } from '@configs/constants';
import { useProfileStore } from '@stores/profile-store';

import AppRoutes from './AppRoutes';
import { ProfileAppMode } from './models/profile-model';

const baseHref = document.querySelector('base').getAttribute('href').replace(/\/$/, '');

// This component is wrapped by BrowserRouter and can use router hooks
const AppContent = () => {
  // Initialize PostHog hook for analytics
  const { identify } = useEvaPostHog();

  // Use React Router's useLocation hook to properly track route changes
  const location = useLocation();
  const pathnameSegments = useMemo(() => location.pathname.split('/').filter(Boolean), [location.pathname]);

  const {
    fetchCodeMap,
    fetchEvYears,
    fetchOtherUserProfile,
    fetchOwnUserProfile,
    profileAppMode,
    updateProfileAppMode,
    contextPersonProfile,
    contextSelectedUserAccount,
    isLoadingUserProfile,
  } = useProfileStore((state) => state);
  const { authStatus, authUserProfile } = useAuthenticationStore((state) => state);
  const { fetchTeamProfile, setIsLoadingTeamProfile } = useTeamProfileStore((state) => state);
  const fetchCountries = useReferenceDataStore((state) => state.fetchCountries);

  // Use the currency initialization hook to set currency based on user account
  useCurrencyInitialization();

  // Set up development authentication cookies
  setupDevAuthCookies();

  // Pure function to extract UUID based on URL segments
  const getUUID = (segments: string[]) => {
    if (segments.length > 0 && segments[0].toLowerCase() === PREFIX_OTHER_USER_URL) {
      return segments[1] || null;
    }
    return null;
  };

  // Pure function to extract team ID from URL segments
  const getTeamID = (segments: string[]) => {
    if (segments.length > 0 && segments[0].toLowerCase() === PREFIX_OTHER_TEAM_URL) {
      return segments[1] || null;
    }
    return null;
  };

  // Fetch team profile based on URL path
  useEffect(() => {
    // Check if this is a team-related path
    const isTeamRelatedPath = pathnameSegments.includes('team');
    const isOtherTeamPath = pathnameSegments[0]?.toLowerCase() === PREFIX_OTHER_TEAM_URL.toLowerCase();

    if (!contextSelectedUserAccount || (!isTeamRelatedPath && !isOtherTeamPath)) {
      return;
    }

    // For other-team routes, get the team ID directly from the URL
    if (isOtherTeamPath) {
      const teamId = getTeamID(pathnameSegments);
      if (teamId) {
        // For other teams, we don't need a specific user ID
        void fetchTeamProfile(teamId, null);
        return;
      }
    }

    const { teams } = contextSelectedUserAccount;

    // contextselecteduseraccount loads as {}
    if (!teams) {
      return;
    }

    // No teams found (empty array), set to forbidden state
    if (!teams.length) {
      setIsLoadingTeamProfile(false);
      return;
    }

    // Check if we already have the team profile loaded for this team
    const { teamProfile } = useTeamProfileStore.getState();
    const teamId = teams[0].teamId;
    const userId = contextSelectedUserAccount.userId;

    // Only fetch if we don't have the team profile or if it's for a different team
    if (!teamProfile || teamProfile.teamId !== teamId) {
      void fetchTeamProfile(teamId, userId);
    }
  }, [contextSelectedUserAccount, pathnameSegments]);

  useEffect(() => {
    if (pathnameSegments.length > 0) {
      const firstSegment = pathnameSegments[0].toLowerCase();
      if (firstSegment === PREFIX_OTHER_USER_URL || firstSegment === PREFIX_OTHER_TEAM_URL) {
        updateProfileAppMode(ProfileAppMode.OTHER_PROFILE);
      }
    }
  }, []);

  useEffect(() => {
    if (authStatus === AuthenticationState.AUTHENTICATED) {
      void (async () => {
        const uuid = getUUID(pathnameSegments);

        // Check URL directly to avoid race condition with profileAppMode state
        if (uuid) {
          await Promise.all([fetchCodeMap(), fetchCountries(), fetchEvYears(), fetchOtherUserProfile(uuid)]);
        } else {
          const effectiveUserId = authUserProfile?.effectiveUser?.userId;
          if (effectiveUserId) {
            await Promise.all([fetchCodeMap(), fetchCountries(), fetchEvYears(), fetchOwnUserProfile(effectiveUserId)]);
          }
        }
      })();
    }
  }, [authStatus, pathnameSegments]);
  // Identify user in PostHog when profile is loaded (own profile mode only)
  useEffect(() => {
    if (profileAppMode === ProfileAppMode.OWN_PROFILE && contextPersonProfile && contextSelectedUserAccount?.userId) {
      identify(contextSelectedUserAccount.userId, {
        name: `${contextPersonProfile.firstName} ${contextPersonProfile.lastName}`,
        email: contextSelectedUserAccount.email,
        user_id: contextSelectedUserAccount.userId,
        shop_id: contextSelectedUserAccount.shopId,
      });
    }
  }, [contextPersonProfile, contextSelectedUserAccount, profileAppMode]);

  // Determine if we're viewing another user's profile based on URL
  const isOtherUserRoute = useMemo(() => {
    if (pathnameSegments.length > 0) {
      const firstSegment = pathnameSegments[0].toLowerCase();
      return firstSegment === PREFIX_OTHER_USER_URL || firstSegment === PREFIX_OTHER_TEAM_URL;
    }
    return false;
  }, [pathnameSegments]);

  // Determine if routes are ready to render
  // - For other user routes: render immediately (ProtectedRoute handles its own loading)
  // - For own profile routes: wait until profile fetch completes
  const isProfileReady = !isLoadingUserProfile && !!contextSelectedUserAccount?.userId;
  const isRoutesReady = isOtherUserRoute || isProfileReady;

  // Don't render anything until auth is complete and profile is loaded
  // EvaLayout handles its own auth loading state, so we return null to let it show
  const isAuthComplete = authStatus === AuthenticationState.AUTHENTICATED;

  // Render content based on loading state:
  // - Auth pending or profile loading: render null (EvaLayout shows its centered loading)
  // - Everything ready: show AppRoutes
  const renderContent = () => {
    if (!isAuthComplete || !isRoutesReady) {
      return null;
    }
    return <AppRoutes />;
  };

  return (
    <EvaLayout
      {...{
        layoutType: EvaLayoutType.HEADER_ONLY,
        antdConfig: {
          theme: {
            token: {
              colorPrimary: '#c80000',
              ...SCREEN_BREAKPOINTS,
            },
          },
        },
      }}
    >
      {renderContent()}
    </EvaLayout>
  );
};

// Main App component that sets up the router
const App = () => {
  return (
    <BrowserRouter
      basename={baseHref}
      // These future flags opt into React Router v7 behavior early
      // See: https://reactrouter.com/en/main/guides/upgrading/v6-to-v7
      future={{
        v7_startTransition: true, // Wraps state updates in React.startTransition
        v7_relativeSplatPath: true, // Updates relative route resolution within splat routes
      }}
    >
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
